import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { ShieldAlert } from "lucide-react";

/**
 * Production-stabilization (Feb 2026):
 * Global modal that surfaces when the axios interceptor detects a 401 mid-
 * session. Replaces the previous behavior where the dashboard would silently
 * blank out after a token went bad.
 *
 * Design:
 *  - Modal is rendered ONCE at the root of <App/> so any page can trigger it.
 *  - Keeps the current page underneath visible — the user does NOT lose
 *    context. They click "Log in again" and we navigate to /login carrying
 *    the current pathname as `?next=` so they bounce straight back after re-
 *    auth.
 *  - On the public auth routes (/login, /register, etc.) we suppress the
 *    modal — those pages handle their own auth state.
 */
const SUPPRESS_ON_PATHS = new Set([
  "/", "/login", "/register", "/forgot-password", "/reset-password",
  "/internal-admin/login", "/internal-admin/reset-password",
]);

export default function SessionExpiredModal() {
  const { sessionExpired, dismissSessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show on public auth pages — they already manage their own state.
  const onAuthPage = SUPPRESS_ON_PATHS.has(location.pathname);
  const open = sessionExpired && !onAuthPage;

  function goLogin() {
    const next = location.pathname + location.search;
    dismissSessionExpired();
    const isInternal = location.pathname.startsWith("/internal-admin");
    const loginPath = isInternal ? "/internal-admin/login" : "/login";
    navigate(`${loginPath}?next=${encodeURIComponent(next)}`, { replace: true });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismissSessionExpired(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="session-expired-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-700 mb-1">
            <ShieldAlert className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-wider font-medium">
              Session expired
            </span>
          </div>
          <DialogTitle className="font-display">
            Please log in again to continue
          </DialogTitle>
          <DialogDescription>
            Your session has timed out or was invalidated by an administrator.
            None of your unsaved work was lost — we&rsquo;ll bring you right back
            to this page after you sign in.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2">
          <Button
            variant="ghost"
            onClick={dismissSessionExpired}
            data-testid="session-expired-dismiss-btn"
          >
            Dismiss
          </Button>
          <Button
            onClick={goLogin}
            data-testid="session-expired-relogin-btn"
          >
            Log in again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
